import { useTranslation } from "react-i18next";
import Joi from "joi";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useEffect, useMemo } from "react";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

const createSchema = (t: (key: string) => string) =>
  Joi.object({
    callback_url: Joi.string()
      .uri()
      .trim()
      .required()
      .pattern(/^https:\/\//)
      .messages({
        "string.empty": t(
          "dashboard.features.loginTemplate.enterCallbackUrlModal.callbackUrlRequired"
        ),
        "any.required": t(
          "dashboard.features.loginTemplate.enterCallbackUrlModal.callbackUrlRequired"
        ),
        "string.uri": t(
          "dashboard.features.loginTemplate.enterCallbackUrlModal.callbackUrlInvalid"
        ),
        "string.pattern.base": t(
          "dashboard.features.loginTemplate.enterCallbackUrlModal.callbackUrlHttpsRequired"
        ),
      })
      .label("Callback URL"),
  }).required();

const EnterCallbackUrlModal = ({
  showModal,
  getCallBackUrlFromCustomer,
  callbackUrlLink,
  disabled,
  isLoading,
}: {
  showModal: boolean;
  getCallBackUrlFromCustomer: (url: string) => void;
  callbackUrlLink: string | null;
  disabled: boolean;
  isLoading: boolean;
}) => {
  const [t] = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canWriteApis = permissions.can(Cap.APIS, Level.WRITE);

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      callback_url: callbackUrlLink || "",
    },
    mode: "onChange",
  });

  const onSubmit = (data: { callback_url: string }) => {
    getCallBackUrlFromCustomer(data.callback_url);
  };

  useEffect(() => {
    reset({ callback_url: callbackUrlLink || "" });
  }, [callbackUrlLink, reset]);

  return (
    <div>
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("dashboard.features.loginTemplate.enterCallbackUrlModal.text1")}
        </h2>
        <p className="mt-0.5 text-sm text-gray-500">
          {t("dashboard.features.loginTemplate.enterCallbackUrlModal.text2")}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className="my-4">
          <CustomInput
            name="callback_url"
            label={
              t(
                "dashboard.features.loginTemplate.enterCallbackUrlModal.text3"
              ) as string
            }
            control={control}
            grayBackground
            error={errors.callback_url && showModal}
            disabled={disabled || !showModal || !canWriteApis}
            helperText={
              showModal ? (errors.callback_url?.message as string) : ""
            }
          />
        </div>

        {canWriteApis && (
          <div className="w-100">
            <CustomButton
              type="submit"
              variant="primary"
              className="ml-auto mr-0"
              disabled={disabled || !showModal || isLoading}
              isLoading={isLoading}
              title={t(
                "dashboard.features.loginTemplate.enterCallbackUrlModal.text5"
              )}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default EnterCallbackUrlModal;
